import { z } from 'zod';
/** RF-35 — cadastro da frota com tecnologia, fonte de km e offset de hodômetro. */
export const VehicleTechnology = {
    DIESEL: 'DIESEL',
    EBUS: 'EBUS',
};
export const KmSource = {
    MANUAL: 'MANUAL',
    TELEMETRY: 'TELEMETRY',
    JB_API: 'JB_API',
};
export const VehicleStatus = {
    AVAILABLE: 'AVAILABLE',
    IN_LINE: 'IN_LINE',
    AWAITING_TRIAGE: 'AWAITING_TRIAGE',
    FIELD_SERVICE: 'FIELD_SERVICE',
    AWAITING_MAINTENANCE: 'AWAITING_MAINTENANCE',
    IN_MAINTENANCE: 'IN_MAINTENANCE',
    AWAITING_PART: 'AWAITING_PART',
    IN_INSPECTION: 'IN_INSPECTION',
    IN_CLEANING: 'IN_CLEANING',
    OUT_OF_SERVICE: 'OUT_OF_SERVICE',
};
/** Classes operacionais da SPTrans, como vêm na Escala de Carros da Transppass. */
export const VehicleClass = {
    BASICO_12M: 'BASICO_12M',
    PADRON_13M: 'PADRON_13M',
    PADRON_15M: 'PADRON_15M',
    EPADRON_13M: 'EPADRON_13M',
    EPADRON_15M: 'EPADRON_15M',
    ARTICULADO_18M: 'ARTICULADO_18M',
    ARTICULADO_23M: 'ARTICULADO_23M',
    EARTICULADO_23M: 'EARTICULADO_23M',
};
export const VEHICLE_CLASS_LABELS = {
    BASICO_12M: 'Básico 12M',
    PADRON_13M: 'Padron 13M',
    PADRON_15M: 'Padron 15M',
    EPADRON_13M: 'ePadron 13M',
    EPADRON_15M: 'ePadron 15M',
    ARTICULADO_18M: 'Articulado 18M',
    ARTICULADO_23M: 'Articulado 23M',
    EARTICULADO_23M: 'eArticulado 23M',
};
/** Classes elétricas: a tecnologia do carro decorre da classe. */
export const ELECTRIC_CLASSES = [
    VehicleClass.EPADRON_13M,
    VehicleClass.EPADRON_15M,
    VehicleClass.EARTICULADO_23M,
];
/** Papel do carro na escala da operação. */
export const FleetRole = {
    OPERACIONAL: 'OPERACIONAL',
    RESERVA: 'RESERVA',
    /** Guincho e carro de treinamento: existem no cadastro, não entram na operação nem no MKBF. */
    APOIO: 'APOIO',
};
export const FLEET_ROLE_LABELS = {
    OPERACIONAL: 'Operacional',
    RESERVA: 'Reserva',
    APOIO: 'Apoio',
};
export const VEHICLE_TECHNOLOGY_LABELS = {
    DIESEL: 'Diesel',
    EBUS: 'eBUS',
};
export const KM_SOURCE_LABELS = {
    MANUAL: 'Lançamento manual',
    TELEMETRY: 'Telemetria',
    JB_API: 'API do JB',
};
export const VEHICLE_STATUS_LABELS = {
    AVAILABLE: 'Disponível',
    IN_LINE: 'Em linha',
    AWAITING_TRIAGE: 'Aguardando triagem',
    FIELD_SERVICE: 'Socorro em campo',
    AWAITING_MAINTENANCE: 'Na fila',
    IN_MAINTENANCE: 'Em manutenção',
    AWAITING_PART: 'Aguardando peça',
    IN_INSPECTION: 'Em inspeção',
    IN_CLEANING: 'Em limpeza',
    OUT_OF_SERVICE: 'Fora de operação',
};
/** Estados que contam como carro disponível para a operação (RN-01). */
export const AVAILABLE_STATUSES = [
    VehicleStatus.AVAILABLE,
    VehicleStatus.IN_LINE,
];
/** Campo numérico opcional: string vazia do formulário vira undefined, não 0. */
export function optionalNumber(schema) {
    return z.preprocess((v) => (v === '' || v === null || v === undefined ? undefined : v), schema.optional());
}
export const plateSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}-?\d[A-Z0-9]\d{2}$/, 'Placa inválida (padrão brasileiro ou Mercosul)')
    .transform((v) => v.replace('-', ''));
export const createVehicleSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(1, 'Informe o prefixo')
        .max(16, 'Prefixo muito longo'),
    plate: plateSchema,
    technology: z.nativeEnum(VehicleTechnology),
    kmSource: z.nativeEnum(KmSource),
    odometerOffset: z.coerce.number().int().default(0),
    manufacturer: z.string().trim().max(60).optional(),
    model: z.string().trim().max(60).optional(),
    modelYear: optionalNumber(z.coerce.number().int().min(1980).max(2100)),
    garageId: z.string().uuid().optional(),
    vehicleClass: z.preprocess((v) => (v === '' ? undefined : v), z.nativeEnum(VehicleClass).optional()),
    lot: z.string().trim().toUpperCase().max(8).optional(),
    fleetRole: z.preprocess((v) => (v === '' ? undefined : v), z.nativeEnum(FleetRole).optional()),
});
export const updateVehicleSchema = createVehicleSchema.partial().extend({
    isActive: z.boolean().optional(),
});
export const vehicleQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(500).default(20),
    search: z.string().trim().max(60).optional(),
    technology: z.nativeEnum(VehicleTechnology).optional(),
    status: z.nativeEnum(VehicleStatus).optional(),
    garageId: z.string().uuid().optional(),
    vehicleClass: z.nativeEnum(VehicleClass).optional(),
    lot: z.string().trim().max(8).optional(),
    onlyActive: z.coerce.boolean().default(true),
});
export const vehicleSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    plate: z.string(),
    technology: z.nativeEnum(VehicleTechnology),
    kmSource: z.nativeEnum(KmSource),
    odometerOffset: z.number().int(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    garageId: z.string().uuid().nullable(),
    vehicleClass: z.nativeEnum(VehicleClass).nullable(),
    lot: z.string().nullable(),
    fleetRole: z.nativeEnum(FleetRole).nullable(),
    status: z.nativeEnum(VehicleStatus),
    currentKm: z.number().int(),
    lastReadingAt: z.string().datetime().nullable(),
    isActive: z.boolean(),
    /** Frota real da Transppass: prefixo do CCO, chassi, carroceria, lotação. */
    ccoPrefix: z.string().nullable().optional(),
    /** Aplicação no cadastro da Transppass: FROTA, GUINCHO ou TREINAMENTO. */
    application: z.string().nullable().optional(),
    chassisCode: z.string().nullable().optional(),
    vin: z.string().nullable().optional(),
    bodyMaker: z.string().nullable().optional(),
    bodyYear: z.number().int().nullable().optional(),
    fabricationYear: z.number().int().nullable().optional(),
    hasAc: z.boolean().optional(),
    seats: z.number().int().nullable().optional(),
    acquiredAt: z.string().datetime().nullable().optional(),
    isImported: z.boolean().optional(),
});
//# sourceMappingURL=vehicle.schemas.js.map